import i18n from '../i18n';
import { createArtifactDashboardApi, DashboardReadApi } from '../components/CustomDashboard/api';
import { session_id } from './session_id';
import { CaseGraphResponse, SavedGraphDetailDTO, SocialGraphModesResponse } from './socialGraphApi';
import { assertSudoWritable } from './sudoState';

export type ArtifactType = 'REPORT' | 'DASHBOARD' | 'SOCIAL_GRAPH';
export type ArtifactBundleStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type ArtifactDisposition = 'inline' | 'attachment';
export type ArtifactArchived = 'hide' | 'include' | 'only';
export type ArtifactSorting =
  | '-published_at'
  | 'published_at'
  | '-created_at'
  | 'created_at'
  | '-title'
  | 'title';

export type ArtifactRefKind = 'social_graph' | 'custom_dashboard';

// live reference to a platform object (saved graph id / custom dashboard uuid)
export interface ArtifactRefDTO {
  kind: ArtifactRefKind;
  target_id: number | string;
}

// file-backed versions carry the file fields; ref-backed versions carry only `ref`
export interface ArtifactVersionDTO {
  id: string;
  artifact_id: string;
  version_no: number;
  file_name: string | null;
  content_type: string | null;
  size_bytes: number | null;
  ref: ArtifactRefDTO | null;
  created_at: string;
}

// current state of the saved graph behind a SOCIAL_GRAPH artifact
export interface ArtifactGraphDTO {
  graph_id: number;
  kind: 'actor' | 'case';
  name: string;
  result: SocialGraphModesResponse | CaseGraphResponse;
}

export interface ArtifactDTO {
  id: string;
  bundle_id: string;
  type: ArtifactType;
  title: string;
  description: string | null;
  position: number;
  created_at: string;
  updated_at: string;
  current_version: ArtifactVersionDTO | null;
}

export interface ArtifactBundleDTO {
  id: string;
  workspace_id: number;
  title: string;
  description: string | null;
  tags: string[];
  status: ArtifactBundleStatus;
  published_at: string | null;
  scheduled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ArtifactBundleListItemDTO extends ArtifactBundleDTO {
  artifact_count: number;
  artifact_types: ArtifactType[];
}

export interface ArtifactBundleDetailDTO extends ArtifactBundleDTO {
  artifacts: ArtifactDTO[];
}

export interface ArtifactBundleListDTO {
  objects: ArtifactBundleListItemDTO[];
  page: number;
  size: number;
  total: number;
}

export interface ArtifactRefRequest {
  kind: ArtifactRefKind;
  target_id: number | string;
}

// one entry of the publish / add-item metadata; files are matched to REPORT items by order
export interface CreateArtifactItem {
  type: ArtifactType;
  title: string;
  description?: string | null;
  position?: number;
  ref?: ArtifactRefRequest;
}

export interface CreateArtifactBundleRequest {
  title: string;
  description?: string | null;
  tags?: string[];
  status?: ArtifactBundleStatus;
  scheduled_at?: string | null;
  artifacts: CreateArtifactItem[];
}

export interface UpdateArtifactBundleRequest {
  title?: string;
  description?: string | null;
  tags?: string[];
  scheduled_at?: string | null;
}

export interface UpdateArtifactItemRequest {
  title?: string;
  description?: string | null;
  position?: number;
}

export interface UploadFileSpec {
  file_name: string;
  size_bytes: number;
  checksum_sha256: string;
}

export interface ArtifactUploadFileSlotDTO {
  file_name: string;
  upload_url: string;
  headers: Record<string, string>;
}

export interface ArtifactUploadSessionDTO {
  upload_token: string;
  expires_at: string;
  files: ArtifactUploadFileSlotDTO[];
}

export interface ArtifactListParams {
  q?: string;
  types?: ArtifactType[];
  tags?: string[];
  dateFrom?: string;
  dateTo?: string;
  archived?: ArtifactArchived;
  sorting?: ArtifactSorting;
  page?: number;
  pageSize?: number;
}

const BASE_URL = '/API/v2/artifacts';

function buildListParams(params: ArtifactListParams): URLSearchParams {
  const urlParams = new URLSearchParams();
  if (params.q) urlParams.set('q', params.q);
  (params.types ?? []).forEach((type) => urlParams.append('type', type));
  (params.tags ?? []).forEach((tag) => urlParams.append('tags', tag));
  // date inputs give date-only values; widen date_to to the end of that day
  if (params.dateFrom) urlParams.set('date_from', `${params.dateFrom}T00:00:00Z`);
  if (params.dateTo) urlParams.set('date_to', `${params.dateTo}T23:59:59Z`);
  if (params.archived && params.archived !== 'hide') urlParams.set('archived', params.archived);
  if (params.sorting) urlParams.set('sorting', params.sorting);
  if (params.page !== undefined) urlParams.set('page', String(params.page));
  if (params.pageSize !== undefined) urlParams.set('page_size', String(params.pageSize));
  return urlParams;
}

type JsonMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

const REQUEST_TIMEOUT_MS = 120_000;
// a 25 MB file on a slow link: generous, but never unbounded
const UPLOAD_TIMEOUT_MS = 10 * 60_000;

// caller's signal (optional) combined with a deadline, so no request can hang forever
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  if (!signal) return timeoutSignal;
  const combined = new AbortController();
  for (const source of [signal, timeoutSignal]) {
    if (source.aborted) combined.abort(source.reason);
    else source.addEventListener('abort', () => combined.abort(source.reason), { once: true });
  }
  return combined.signal;
}

// Carries the API's own explanation so the UI can show it: a rejected upload has
// an actionable reason ("only PDF and HTML are accepted") that a generic toast hides.
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;

  constructor(status: number, detail?: string) {
    super('Client error: ' + (detail || `Request failed with status ${status}`));
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail || undefined;
  }
}

// A fetch that never gets a response carries no `detail`, so the toast would fall back to
// the generic text for what is really an unreachable server. Timeouts and caller-driven
// aborts already have their own handling and pass through untouched.
async function fetchOrExplain(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    const name = (error as Error)?.name;
    if (name === 'TimeoutError' || name === 'AbortError') throw error;
    throw new ApiError(0, i18n.t('Could not reach the server. Check your connection and retry.'));
  }
}

async function fetchInWorkspace(
  method: JsonMethod,
  url: string,
  workspaceId: number | undefined,
  body?: unknown,
  signal?: AbortSignal,
): Promise<any> {
  // Same rule as API.fetch: no writes from a tab whose sudo was switched elsewhere.
  assertSudoWritable(method);

  const headers: Record<string, string> = { 'X-Request-ID': session_id };
  // send the override only when there is one, else the token's own workspace applies
  if (workspaceId !== undefined) headers['workspace-id'] = String(workspaceId);
  // multipart bodies get their boundary from the browser
  if (!(body instanceof FormData)) headers['Content-Type'] = 'application/json';
  const token = localStorage.getItem('token');
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetchOrExplain(url, {
    method,
    headers,
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    signal: withTimeout(signal, REQUEST_TIMEOUT_MS),
  });

  if (response.status === 401 || response.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('workspace_id');
    localStorage.removeItem('workspace_name');
    window.location.href = '/';
  }

  if (!response.ok) {
    let detail = '';
    try {
      const data = await response.json();
      if (typeof data?.detail === 'string') {
        detail = data.detail;
      } else if (Array.isArray(data?.detail)) {
        // FastAPI reports validation failures as a list of {loc, msg}
        detail = data.detail
          .map((entry: { msg?: string }) => entry?.msg)
          .filter(Boolean)
          .join('; ');
      }
    } catch {
      // non-JSON error body
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return null;
  return response.json();
}

export interface ArtifactRequestOptions {
  workspaceId?: number;
  signal?: AbortSignal;
}

function storedWorkspaceId(): number | undefined {
  const stored = localStorage.getItem('workspace_id');
  return stored !== null ? Number(stored) : undefined;
}

// Every artifact call goes through the same path: same headers, token and 401 handling as
// API.fetch, but it keeps the API's own explanation on the error. API.fetch flattens a
// rejection into an Error message, which the toast cannot tell from any other failure —
// so an upload refused for a nameable reason would surface as "could not be loaded".
function request(
  method: JsonMethod,
  url: string,
  body: unknown,
  { workspaceId, signal }: ArtifactRequestOptions,
): Promise<any> {
  return fetchInWorkspace(method, url, workspaceId ?? storedWorkspaceId(), body, signal);
}

function requestForm(url: string, form: FormData, { workspaceId, signal }: ArtifactRequestOptions): Promise<any> {
  return fetchInWorkspace('POST', url, workspaceId ?? storedWorkspaceId(), form, signal);
}

async function sha256Hex(file: File): Promise<string> {
  // A picked file is a handle, not a copy: moving, renaming or re-saving it between the
  // file dialog and the publish click makes the read fail. Say which file and what to do,
  // because nothing has reached the network yet and the API has no reason to offer.
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    throw new ApiError(
      0,
      i18n.t('{{name}} could not be read. Select the file again and retry.', { name: file.name }),
    );
  }
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function metadataForm(metadata: unknown, uploadToken?: string): FormData {
  const form = new FormData();
  form.append('metadata', JSON.stringify(metadata));
  if (uploadToken) form.append('upload_token', uploadToken);
  return form;
}

// SocialGraphPOC renders saved-graph detail rows; the artifact endpoint returns the
// live result without the row metadata, so fill in what the viewer never reads
export function toSavedGraphDetail(graph: ArtifactGraphDTO): SavedGraphDetailDTO {
  return {
    id: graph.graph_id,
    name: graph.name,
    kind: graph.kind,
    source_type: null,
    source_id: null,
    status: 'ready',
    created_at: '',
    task_id: null,
    user: null,
    result: graph.result,
    error: null,
  };
}

export const artifactsApi = {
  async listBundles(params: ArtifactListParams, signal?: AbortSignal): Promise<ArtifactBundleListDTO> {
    return request('GET', `${BASE_URL}?${buildListParams(params).toString()}`, undefined, { signal });
  },

  async listRegistry(
    params: ArtifactListParams & { workspaceId?: number },
    signal?: AbortSignal,
  ): Promise<ArtifactBundleListDTO> {
    const urlParams = buildListParams(params);
    if (params.workspaceId !== undefined) urlParams.set('workspace_id', String(params.workspaceId));
    return request('GET', `${BASE_URL}/registry?${urlParams.toString()}`, undefined, { signal });
  },

  async getBundle(bundleId: string, options: ArtifactRequestOptions = {}): Promise<ArtifactBundleDetailDTO> {
    return request('GET', `${BASE_URL}/${bundleId}`, undefined, options);
  },

  async getContentUrl(
    bundleId: string,
    artifactId: string,
    disposition: ArtifactDisposition,
    options: ArtifactRequestOptions = {},
  ): Promise<string> {
    const url = `${BASE_URL}/${bundleId}/items/${artifactId}/content?disposition=${disposition}&redirect=false`;
    const response: { url: string } = await request('GET', url, undefined, options);
    return response.url;
  },

  // live state of the graph behind a SOCIAL_GRAPH artifact, served under artifacts:read
  async getGraph(
    bundleId: string,
    artifactId: string,
    options: ArtifactRequestOptions = {},
  ): Promise<ArtifactGraphDTO> {
    return request('GET', `${BASE_URL}/${bundleId}/items/${artifactId}/graph`, undefined, options);
  },

  // read-only dashboard API bound to a DASHBOARD artifact: the same viewer code path
  // as /custom-dashboards, but through the artifact proxies (no dashboards menu role)
  dashboardApi(bundleId: string, artifactId: string, options: ArtifactRequestOptions = {}): DashboardReadApi {
    const base = `${BASE_URL}/${bundleId}/items/${artifactId}/dashboard`;
    return createArtifactDashboardApi((method, path, body) => request(method, `${base}${path}`, body, options));
  },

  // --- publishing (artifacts:publish + super admin) ---

  // Direct-to-S3 upload: presign one slot per file, PUT the bytes with exactly the
  // signed headers, and hand the manifest token to the publish call. Returns the
  // token; undefined when there is nothing to upload.
  async uploadFiles(files: File[], options: ArtifactRequestOptions = {}): Promise<string | undefined> {
    if (files.length === 0) return undefined;
    const specs: UploadFileSpec[] = await Promise.all(
      files.map(async (file) => ({
        file_name: file.name,
        size_bytes: file.size,
        checksum_sha256: await sha256Hex(file),
      })),
    );
    const session: ArtifactUploadSessionDTO = await request('POST', `${BASE_URL}/uploads`, { files: specs }, options);
    await Promise.all(
      session.files.map(async (slot, index) => {
        const response = await fetchOrExplain(slot.upload_url, {
          method: 'PUT',
          headers: slot.headers,
          body: files[index],
          signal: withTimeout(options.signal, UPLOAD_TIMEOUT_MS),
        });
        if (!response.ok) {
          throw new ApiError(
            response.status,
            i18n.t('Upload of {{name}} failed. Please try again.', { name: slot.file_name }),
          );
        }
      }),
    );
    return session.upload_token;
  },

  async publishBundle(
    metadata: CreateArtifactBundleRequest,
    files: File[],
    options: ArtifactRequestOptions = {},
  ): Promise<ArtifactBundleDetailDTO> {
    const uploadToken = await artifactsApi.uploadFiles(files, options);
    return requestForm(BASE_URL, metadataForm(metadata, uploadToken), options);
  },

  async addItem(
    bundleId: string,
    item: CreateArtifactItem,
    file: File | null,
    options: ArtifactRequestOptions = {},
  ): Promise<ArtifactDTO> {
    const uploadToken = await artifactsApi.uploadFiles(file ? [file] : [], options);
    return requestForm(`${BASE_URL}/${bundleId}/items`, metadataForm(item, uploadToken), options);
  },

  async updateItem(
    bundleId: string,
    artifactId: string,
    data: UpdateArtifactItemRequest,
    options: ArtifactRequestOptions = {},
  ): Promise<ArtifactDTO> {
    return request('PATCH', `${BASE_URL}/${bundleId}/items/${artifactId}`, data, options);
  },

  async deleteItem(bundleId: string, artifactId: string, options: ArtifactRequestOptions = {}): Promise<void> {
    await request('DELETE', `${BASE_URL}/${bundleId}/items/${artifactId}`, undefined, options);
  },

  async updateBundle(
    bundleId: string,
    data: UpdateArtifactBundleRequest,
    options: ArtifactRequestOptions = {},
  ): Promise<ArtifactBundleDTO> {
    return request('PATCH', `${BASE_URL}/${bundleId}`, data, options);
  },

  async publishDraft(bundleId: string, options: ArtifactRequestOptions = {}): Promise<ArtifactBundleDTO> {
    return request('POST', `${BASE_URL}/${bundleId}/publish`, undefined, options);
  },

  async archiveBundle(bundleId: string, options: ArtifactRequestOptions = {}): Promise<void> {
    await request('DELETE', `${BASE_URL}/${bundleId}`, undefined, options);
  },

  async restoreBundle(bundleId: string, options: ArtifactRequestOptions = {}): Promise<ArtifactBundleDTO> {
    return request('POST', `${BASE_URL}/${bundleId}/restore`, undefined, options);
  },
};
